<template>
  <div class="billing-operation-mode">
    <operation-context-alert>
      {{ productContextText }}
    </operation-context-alert>

    <operation-summary-strip :items="summaryItems" />

    <operation-panel
      :title="productPanelTitle"
      :subtitle="productPanelSubtitle"
      tone="primary"
      :chip-label="productPanelChipLabel"
      chip-color="primary"
    >
      <div class="operation-field-grid operation-field-grid--three">
        <div class="operation-field">
          <div class="operation-field__label">Ürün</div>
          <rs-select-product
            class="operation-field-input operation-field-input--compact operation-field-input--product"
            :value="product.product_id"
            label=""
            placeholder="Ürün Seçiniz"
            persistent-placeholder
            :rules="[rules.required]"
            @input="updateProductField('product_id', $event)"
            @change:item="handleProductSelected"
          />
          <div class="operation-field__hint">Aktif standart ürün</div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">Tutar / BB</div>
          <rs-text-field
            class="operation-field-input operation-field-input--compact"
            :value="product.amount"
            label=""
            prefix="₺"
            type="number"
            step="0.01"
            :rules="[
              rules.required,
              rules.min(product.amount, 0),
              rules.max(product.amount, 999999.99),
            ]"
            hide-details
            @input="updateProductField('amount', $event)"
          />
          <div class="operation-field__hint">{{ productAmountHint }}</div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">Faturalandırma</div>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div
                class="operation-field__readonly operation-field__readonly--clickable"
                v-bind="attrs"
                v-on="on"
              >
                <span>{{ billingTypeLabel }}</span>
                <v-icon small color="#757575">mdi-menu-down</v-icon>
              </div>
            </template>
            <v-list dense>
              <v-list-item @click="setBillingType(false)">
                <v-list-item-title>Aylık</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setBillingType(true)">
                <v-list-item-title>Yıllık</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="operation-field__hint">Aylık veya yıllık</div>
        </div>
      </div>

      <div class="operation-field-grid operation-field-grid--two">
        <div class="operation-field">
          <div class="operation-field__label">Ürün başlangıç</div>
          <rs-datepicker
            class="operation-field-input operation-field-input--compact"
            :value="product.starts_on"
            label=""
            :rules="[rules.required]"
            hide-details
            @input="updateProductField('starts_on', $event)"
          />
          <div class="operation-field__hint">Doğrulanan ilişki başlangıcı</div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">Ürün bitiş</div>
          <rs-datepicker
            class="operation-field-input operation-field-input--compact"
            :value="product.ends_on"
            label=""
            :disabled="!product.starts_on"
            :min="product.starts_on"
            hide-details
            @input="updateProductField('ends_on', $event)"
          />
          <div class="operation-field__hint">Boş bırakılabilir</div>
        </div>
      </div>
    </operation-panel>

    <operation-blocked-reasons
      v-if="visibleBlockedReasons.length"
      :reasons="visibleBlockedReasons"
    />
  </div>
</template>

<script>
import validation from "@/core/validation";
import OperationBlockedReasons from "../shared/OperationBlockedReasons.vue";
import OperationContextAlert from "../shared/OperationContextAlert.vue";
import OperationPanel from "../shared/OperationPanel.vue";
import OperationSummaryStrip from "../shared/OperationSummaryStrip.vue";

export default {
  name: "ProductOperationMode",
  components: {
    OperationBlockedReasons,
    OperationContextAlert,
    OperationPanel,
    OperationSummaryStrip,
  },
  props: {
    draft: {
      type: Object,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      rules: validation,
    };
  },
  computed: {
    product() {
      return this.draft.product || {};
    },
    customerName() {
      return this.preview?.customer?.name || "Seçilen müşteri";
    },
    clusterName() {
      return this.preview?.cluster?.name || "Seçilen TYA";
    },
    relations() {
      return this.preview?.relations || [];
    },
    customerProducts() {
      return this.preview?.customer_products || [];
    },
    blockedReasons() {
      return this.preview?.resolution_plan?.blocked_reasons || [];
    },
    visibleBlockedReasons() {
      return this.blockedReasons.filter(
        (reason) => !String(reason || "").startsWith("İlişki:")
      );
    },
    riskType() {
      return (
        this.preview?.source?.issue_type || this.preview?.risk_type || null
      );
    },
    isMissingProductOperation() {
      return this.riskType === "missing_product";
    },
    isEndedProductOperation() {
      return this.riskType === "ended_product";
    },
    productContextText() {
      if (this.isEndedProductOperation) {
        return "Önceki standart ürün dönemi bitmiş. Yeni ürün dönemi son kapanışın ertesi gününden başlatılır.";
      }

      if (this.isMissingProductOperation) {
        return "Mevcut müşteri ilişkisi korunur. Standart ürün dönemi tamamlandığında son policy önizlemesi alınır.";
      }

      return "Ürün başlangıcı kontrol edilen müşteri ilişkisiyle hizalanır. Ürün adımı tamamlandığında son policy önizlemesi alınır.";
    },
    productPanelTitle() {
      if (this.isEndedProductOperation) {
        return "Ürünü uzat";
      }

      return "Ürün ekle";
    },
    productPanelSubtitle() {
      if (this.isEndedProductOperation) {
        return "Yeni ürün, son kapanışın ertesi günüyle başlar.";
      }

      return this.isMissingProductOperation
        ? "Ürün, tutar, tip ve dönem seçilir."
        : "Ürün, tutar, faturalandırma tipi ve dönem seçilir.";
    },
    productPanelChipLabel() {
      if (this.isEndedProductOperation) {
        return "Devam";
      }

      return this.isMissingProductOperation ? "Adım 1" : "Adım 2";
    },
    billingTypeLabel() {
      return this.product.is_yearly ? "Yıllık" : "Aylık";
    },
    productAmountHint() {
      return this.product.is_yearly ? "Yıllık fiyat" : "Aylık fiyat";
    },
    summaryItems() {
      return [
        {
          label: "Fatura sahibi",
          value: this.customerName,
        },
        {
          label: "TYA",
          value: this.clusterName,
        },
        {
          label: "Geçmiş ilişki",
          value:
            this.relations.length > 0
              ? `${this.relations.length} kayıt`
              : "Yok",
        },
        {
          label: "Ürün geçmişi",
          value:
            this.customerProducts.length > 0
              ? `${this.customerProducts.length} kayıt`
              : "Yok",
        },
      ];
    },
  },
  methods: {
    updateProductField(field, value) {
      this.$emit("update-draft", {
        namespace: "product",
        field,
        value,
      });
    },
    handleProductSelected(product) {
      this.$emit("product-selected", product || null);
    },
    handleBillingTypeChanged(isYearly) {
      this.updateProductField("is_yearly", Boolean(isYearly));
      this.$emit("billing-type-changed", Boolean(isYearly));
    },
    setBillingType(isYearly) {
      this.handleBillingTypeChanged(isYearly);
    },
  },
};
</script>

<style src="../shared/operation-fields.css"></style>
